// frontend/src/components/admin/tenants/ouTree/EnvironmentRow.tsx
/**
 * Linha de um ambiente (env) dentro de um card de UO pai.
 *
 * Layout: [ENV pill] [tenant name] [account_id link] [status badge]
 *         [capability chips] [timezone trigger] [active switch] [⋮ menu]
 *
 * Quando `tenant === null`, renderiza placeholder "— sem contas —" no lugar
 * dos campos pos-env-pill. Util quando uma Sub-UO esta vazia (ex: VPN).
 *
 * Substitui o componente antigo `EnvironmentRow.tsx` (deletado) preservando
 * a linguagem visual aprovada pelo usuario.
 */
import { Group, Text, Anchor, Code, Tooltip, Switch, Box, Badge, ActionIcon, Menu } from '@mantine/core'
import { RenameModal } from '../modals/RenameModal'
import { useState } from 'react'
import {
  IconExternalLink,
  IconClock,
  IconDots,
  IconPencil,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconArrowRight,
  IconWorldPin,
} from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'
import type { TenantRow } from '../TenantsTable'
import { TimezoneMenu } from '../TimezoneMenu'
import { TenantBudgetIcon } from '@/components/admin/tenants/TenantBudgetIcon'
import { SeloDoAmbiente } from '@/components/certificacao/SeloDoAmbiente'
import { useSelosDeCertificacao } from '@/hooks/useCertificacoes'
import {
  STATUS_LABELS,
  STATUS_COLORS,
  getLeafStatus,
  awsOrgConsoleAccountUrl,
} from './leafStatus'

interface ToolDef {
  field: keyof TenantRow
  label: string
  short: string
  color: string
}

// `has_pci` SAIU desta lista (TASK-11960) e nao volta: a coluna e' CACHE do vinculo em
// `certificado_ambientes`, e o `PATCH` de flags RECUSA o campo com 422 desde a Onda 1
// (MIGRATION_346). O chip continuava clicavel e a pessoa recebia erro ao clicar — quem muda
// PCI e' a tela /admin/certificacoes. No lugar dele a linha mostra o SELO, que e' leitura.
const FIELD_TOOLS: ToolDef[] = [
  { field: 'has_terraform', label: 'Terraform', short: 'TF', color: 'violet' },
  { field: 'has_waf', label: 'WAF', short: 'WAF', color: 'orange' },
  { field: 'has_pentest', label: 'Pentest', short: 'PEN', color: 'cyan' },
  // `tenants.has_soc` — AUTORIDADE (decisao do proprietario, 27/08/2026):
  // desligar desativa o time de SOC do tenant E as regras de roteamento dele
  // (`desligar_soc_do_tenant`/`religar_soc_do_tenant`). Fase 5b / Task 5.
  // `grape` nao colide com nenhuma das quatro acima nem com os chips
  // seguintes (green/blue/indigo/teal).
  { field: 'has_soc', label: 'SOC', short: 'SOC', color: 'grape' },
]

interface Props {
  envName: string
  envColor: string
  tenant: TenantRow | null
  showSeparator: boolean
  /**
   * Quando true, substitui a env pill por uma faixa vertical fina (4px) na
   * mesma cor do env. Usado quando o header do grupo (SubOuCollapseGroup)
   * ja exibe a pill, evitando redundancia visual nas rows filhas.
   */
  hideEnvPill?: boolean
  onToggleField: (id: number, field: string, value: boolean) => void
  onToggleZabbix: (tenantId: number, enabled: boolean) => void
  onToggleGitlab: (tenantId: number, currentlyEnabled: boolean) => void
  onToggleWhatsApi: (tenantId: number, enabled: boolean) => void
  onUpdateTimezone?: (tenantId: number, timezone: string | null) => void
  onEditRegions?: (tenant: TenantRow) => void
  onProvisioningFailedClick?: (tenant: TenantRow) => void
  fieldPendingIds?: Set<number>
  zabbixLoading?: boolean
  gitlabLoading?: boolean
  whatsapiPendingIds?: Set<number>
  timezonePendingIds?: Set<number>
}

export function EnvironmentRow({
  envName,
  envColor,
  tenant,
  showSeparator,
  hideEnvPill,
  onToggleField,
  onToggleZabbix,
  onToggleGitlab,
  onToggleWhatsApi,
  onUpdateTimezone,
  onEditRegions,
  onProvisioningFailedClick,
  fieldPendingIds,
  zabbixLoading,
  gitlabLoading,
  whatsapiPendingIds,
  timezonePendingIds,
}: Props) {
  const navigate = useNavigate()
  // O mapa inteiro numa consulta, compartilhada por TODAS as linhas pela chave do
  // react-query — a alternativa era arrastar o mapa por quatro componentes de prop.
  // 403 de quem nao tem `certificacoes:read` nao e' erro de tela: o selo so' nao aparece.
  const { data: selos } = useSelosDeCertificacao()
  const accountUrl = tenant ? awsOrgConsoleAccountUrl(tenant.account_id) : null
  const status = tenant ? getLeafStatus(tenant) : null
  const isFieldPending = tenant ? fieldPendingIds?.has(tenant.id) ?? false : false
  const isWhatsApiPending = tenant ? whatsapiPendingIds?.has(tenant.id) ?? false : false
  const isTimezonePending = tenant ? timezonePendingIds?.has(tenant.id) ?? false : false

  const testId = tenant ? `env-row-${tenant.id}` : `env-row-empty-${envName.toLowerCase()}`
  const [renameOpen, setRenameOpen] = useState(false)

  return (
    <Box
      px="md"
      py="sm"
      data-testid={testId}
      style={{
        borderBottom: showSeparator
          ? '1px solid var(--mantine-color-default-border)'
          : 'none',
        opacity: tenant ? (tenant.is_active ? 1 : 0.55) : 0.75,
        transition: 'background 120ms',
      }}
    >
      <Group gap="md" wrap="nowrap" align="center">
        {hideEnvPill ? (
          /* faixa vertical fina (4px) no lugar da pill — header ja tem o pill */
          <Box
            data-testid={`env-strip-${envName.toLowerCase()}`}
            style={{
              width: 4,
              alignSelf: 'stretch',
              minHeight: 24,
              borderRadius: 2,
              background: `var(--mantine-color-${envColor}-6)`,
              flexShrink: 0,
            }}
          />
        ) : (
          /* env pill */
          <Box
            style={{
              minWidth: 56,
              padding: '3px 10px',
              borderRadius: 4,
              background: `var(--mantine-color-${envColor}-light)`,
              color: `var(--mantine-color-${envColor}-7)`,
              fontFamily: 'monospace',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.5,
              textAlign: 'center',
              flexShrink: 0,
            }}
          >
            {envName.toUpperCase()}
          </Box>
        )}

        {tenant === null ? (
          // Empty Sub-OU placeholder
          <Text size="xs" c="dimmed" fs="italic" style={{ flex: 1 }}>
            — sem contas —
          </Text>
        ) : (
          <>
            {/* tenant name */}
            <Box style={{ minWidth: 140, maxWidth: 220, flexShrink: 0 }}>
              <Tooltip label={tenant.name} withArrow openDelay={400}>
                <Text
                  size="sm"
                  fw={500}
                  truncate
                  style={{ letterSpacing: -0.1 }}
                >
                  {tenant.name}
                </Text>
              </Tooltip>
            </Box>

            {/* account id */}
            <Box style={{ minWidth: 170, flexShrink: 0 }}>
              {tenant.account_id ? (
                accountUrl ? (
                  <Anchor
                    href={accountUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="never"
                  >
                    <Code
                      style={{
                        fontSize: 11,
                        color: 'var(--mantine-color-blue-6)',
                      }}
                    >
                      {tenant.account_id}{' '}
                      <IconExternalLink size={10} style={{ marginLeft: 2 }} />
                    </Code>
                  </Anchor>
                ) : (
                  <Code style={{ fontSize: 11 }}>{tenant.account_id}</Code>
                )
              ) : (
                <Text size="xs" c="dimmed" ff="monospace">
                  — sem conta —
                </Text>
              )}
            </Box>

            {/* status badge */}
            <Box style={{ minWidth: 90, flexShrink: 0 }}>
              {status === 'failed' && onProvisioningFailedClick ? (
                <Tooltip label="Clique para ver erro" withArrow>
                  <Badge
                    color={STATUS_COLORS[status]}
                    variant="light"
                    size="sm"
                    radius="sm"
                    style={{ textTransform: 'none', fontWeight: 600, cursor: 'pointer' }}
                    onClick={() => onProvisioningFailedClick(tenant)}
                  >
                    {STATUS_LABELS[status]}
                  </Badge>
                </Tooltip>
              ) : (
                <Badge
                  color={STATUS_COLORS[status!]}
                  variant="light"
                  size="sm"
                  radius="sm"
                  style={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {STATUS_LABELS[status!]}
                </Badge>
              )}
            </Box>

            {/* tool chips */}
            <Group gap={4} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
              {FIELD_TOOLS.map((tool) => (
                <ToolChip
                  key={tool.field}
                  short={tool.short}
                  label={tool.label}
                  color={tool.color}
                  enabled={!!tenant[tool.field]}
                  loading={isFieldPending}
                  onToggle={() =>
                    onToggleField(tenant.id, tool.field as string, !tenant[tool.field])
                  }
                />
              ))}
              <SeloDoAmbiente selo={selos?.[String(tenant.id)]} tenantId={tenant.id} />
              <Box style={{ width: 8 }} />
              <ToolChip
                short="ZAB"
                label="Zabbix"
                color="green"
                enabled={!!tenant.zabbix_enabled}
                loading={zabbixLoading}
                onToggle={() => onToggleZabbix(tenant.id, !tenant.zabbix_enabled)}
              />
              <ToolChip
                short="GIT"
                label="GitLab"
                color="blue"
                enabled={!!tenant.git_enabled}
                loading={gitlabLoading}
                onToggle={() => onToggleGitlab(tenant.id, !!tenant.git_enabled)}
              />
              <ToolChip
                short="WHT"
                label="WhatsApi"
                color="green"
                enabled={!!tenant.whatsapi_enabled}
                loading={isWhatsApiPending}
                onToggle={() => onToggleWhatsApi(tenant.id, !tenant.whatsapi_enabled)}
              />
              <ToolChip
                short="ZTNA"
                label="ZTNA"
                color="indigo"
                enabled={!!tenant.ztna_enabled}
                onToggle={() => navigate('/admin/ztna')}
                tooltipOverride={
                  tenant.ztna_enabled
                    ? `ZTNA: ${tenant.ztna_apps_count ?? 0} app(s) onboarded (clique para abrir)`
                    : 'ZTNA: nenhum app onboarded (clique para abrir wizard)'
                }
              />
              <TenantBudgetIcon tenantId={tenant.id} tenantName={tenant.name} />
            </Group>

            {/* timezone trigger */}
            {onUpdateTimezone ? (
              <Menu shadow="md" width={260} position="bottom-end" withinPortal>
                <Menu.Target>
                  <Tooltip
                    label={
                      tenant.timezone
                        ? `Fuso: ${tenant.timezone}`
                        : 'Fuso: padrão do sistema'
                    }
                    withArrow
                  >
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="md"
                      aria-label="Fuso horário"
                      data-testid={`tz-trigger-${tenant.id}`}
                      disabled={isTimezonePending}
                    >
                      <IconClock size={14} />
                    </ActionIcon>
                  </Tooltip>
                </Menu.Target>
                <Menu.Dropdown>
                  <TimezoneMenu
                    currentTimezone={tenant.timezone ?? null}
                    disabled={isTimezonePending}
                    onSelect={(tz) => onUpdateTimezone(tenant.id, tz)}
                  />
                </Menu.Dropdown>
              </Menu>
            ) : null}

            {/* active switch */}
            <Tooltip
              label={tenant.is_active ? 'Desativar ambiente' : 'Ativar ambiente'}
              position="top"
              withArrow
            >
              <Switch
                checked={tenant.is_active}
                onChange={() =>
                  onToggleField(tenant.id, 'is_active', !tenant.is_active)
                }
                color="teal"
                size="sm"
                disabled={isFieldPending}
                aria-label={`Alternar ativo de ${tenant.name}`}
                data-testid={`active-switch-${tenant.id}`}
              />
            </Tooltip>

            {/* overflow menu */}
            <Menu shadow="md" width={220} position="bottom-end" withinPortal>
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="md"
                  aria-label="Ações da conta"
                  data-testid={`account-menu-trigger-${tenant.id}`}
                >
                  <IconDots size={14} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>{tenant.name}</Menu.Label>
                <Menu.Item
                  leftSection={tenant.is_active ? <IconX size={14} /> : <IconCheck size={14} />}
                  disabled={isFieldPending}
                  onClick={() => onToggleField(tenant.id, 'is_active', !tenant.is_active)}
                >
                  {tenant.is_active ? 'Desativar' : 'Ativar'}
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  leftSection={<IconPencil size={14} />}
                  onClick={() => setRenameOpen(true)}
                >
                  Renomear ambiente
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconArrowRight size={14} />}
                  onClick={() => navigate(`/admin/tenants/${tenant.id}`)}
                >
                  Detalhes do tenant
                </Menu.Item>
                {accountUrl && (
                  <Menu.Item
                    leftSection={<IconExternalLink size={14} />}
                    component="a"
                    href={accountUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Abrir na AWS Console
                  </Menu.Item>
                )}
                {onEditRegions && (
                  <>
                    <Menu.Divider />
                    <Menu.Item
                      leftSection={<IconWorldPin size={14} />}
                      onClick={() => onEditRegions(tenant)}
                      data-testid={`aws-regions-${tenant.id}`}
                    >
                      Regiões AWS
                    </Menu.Item>
                  </>
                )}
                {status === 'failed' && onProvisioningFailedClick && (
                  <>
                    <Menu.Divider />
                    <Menu.Item
                      color="red"
                      leftSection={<IconAlertCircle size={14} />}
                      onClick={() => onProvisioningFailedClick(tenant)}
                    >
                      Ver erro de provisionamento
                    </Menu.Item>
                  </>
                )}
              </Menu.Dropdown>
            </Menu>
          </>
        )}
      </Group>
      {tenant && renameOpen && (
        <RenameModal
          opened={renameOpen}
          tipo="ambiente"
          tenantId={tenant.id}
          nomeAtual={envName}
          onClose={() => setRenameOpen(false)}
        />
      )}
    </Box>
  )
}

function ToolChip({
  short,
  label,
  color,
  enabled,
  loading,
  onToggle,
  tooltipOverride,
}: {
  short: string
  label: string
  color: string
  enabled: boolean
  loading?: boolean
  onToggle: () => void
  /** Quando passado, substitui o tooltip default
   * `${label}: ativo/inativo (clique para alternar)`. Util para chips
   * informacionais (ZTNA) onde click navega em vez de alternar. */
  tooltipOverride?: string
}) {
  return (
    <Tooltip
      label={
        tooltipOverride ??
        `${label}: ${enabled ? 'ativo' : 'inativo'} (clique para alternar)`
      }
      position="top"
      withArrow
    >
      <button
        type="button"
        onClick={onToggle}
        disabled={loading}
        aria-pressed={enabled}
        aria-label={`Alternar ${label}`}
        style={{
          padding: '3px 8px',
          borderRadius: 4,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.6,
          fontFamily: 'monospace',
          border: enabled
            ? `1px solid var(--mantine-color-${color}-6)`
            : '1px solid var(--mantine-color-default-border)',
          background: enabled
            ? `var(--mantine-color-${color}-light)`
            : 'transparent',
          color: enabled
            ? `var(--mantine-color-${color}-8)`
            : 'var(--mantine-color-dimmed)',
          cursor: loading ? 'wait' : 'pointer',
          opacity: loading ? 0.5 : 1,
          transition: 'background 120ms, border-color 120ms, color 120ms',
        }}
      >
        {short}
      </button>
    </Tooltip>
  )
}
